import { useState, useEffect, useRef } from 'react'
import { Plus, RefreshCw, Search, ChevronDown, StickyNote, FileEdit } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
} from '@dnd-kit/sortable'
import {
  CanoneRicorsivo,
  ListinoItem,
  CanoneUpdate,
  CanoneLiberoCrea,
  FrequenzaUnita,
  FREQUENZA_LABELS,
} from '../../types/offerte'
import { SortableCanoneRow } from './SortableCanoneRow'

interface CanoniSectionProps {
  canoni: CanoneRicorsivo[]
  listino: ListinoItem[]
  totaleCanoni: number
  onAddCanone: (listinoId: string, quantita: number, frequenzaValore: number, frequenzaUnita: FrequenzaUnita, giorniPreavviso: number) => void
  onAddNota: (descrizione: string) => void
  onAddCanoneLibero: (data: CanoneLiberoCrea) => void
  onUpdateCanone: (canoneId: string, updates: CanoneUpdate) => void
  onUpdateNota: (canoneId: string, descrizione: string) => void
  onDeleteCanone: (canoneId: string) => void
  onReorder: (canoneIds: string[]) => void
  disabled?: boolean
}

export function CanoniSection({
  canoni,
  listino,
  totaleCanoni,
  onAddCanone,
  onAddNota,
  onAddCanoneLibero,
  onUpdateCanone,
  onUpdateNota,
  onDeleteCanone,
  onReorder,
  disabled,
}: CanoniSectionProps) {
  const [showAddForm, setShowAddForm] = useState(false)
  const [showNotaForm, setShowNotaForm] = useState(false)
  const [showLiberoForm, setShowLiberoForm] = useState(false)
  const [selectedListino, setSelectedListino] = useState<ListinoItem | null>(null)
  const [quantita, setQuantita] = useState('1')
  const [searchListino, setSearchListino] = useState('')
  const [showListinoDropdown, setShowListinoDropdown] = useState(false)
  const [notaText, setNotaText] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Campi frequenza per form listino
  const [freqValore, setFreqValore] = useState('1')
  const [freqUnita, setFreqUnita] = useState<FrequenzaUnita>('mesi')
  const [giorniPreavviso, setGiorniPreavviso] = useState('30')

  // Form canone libero
  const [liberoForm, setLiberoForm] = useState({
    descrizione: '',
    unita_misura: 'pz',
    prezzo_unitario: '',
    quantita: '1',
    sconto_percentuale: '0',
    frequenza_valore: '1',
    frequenza_unita: 'mesi' as FrequenzaUnita,
    giorni_preavviso: '30',
  })

  const formatCurrency = (value: number | string) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(Number(value) || 0)
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const availableListino = listino.filter(l =>
    l.attivo &&
    (l.codice.toLowerCase().includes(searchListino.toLowerCase()) ||
      l.descrizione.toLowerCase().includes(searchListino.toLowerCase()))
  )

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowListinoDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleAdd = () => {
    if (!selectedListino || !quantita) return
    onAddCanone(
      selectedListino.id,
      parseFloat(quantita),
      parseInt(freqValore) || 1,
      freqUnita,
      parseInt(giorniPreavviso) || 30,
    )
    setSelectedListino(null)
    setQuantita('1')
    setSearchListino('')
    setFreqValore('1')
    setFreqUnita('mesi')
    setGiorniPreavviso('30')
    setShowAddForm(false)
  }

  const handleAddNota = () => {
    if (!notaText.trim()) return
    onAddNota(notaText.trim())
    setNotaText('')
    setShowNotaForm(false)
  }

  const handleAddLibero = () => {
    const prezzo = parseFloat(liberoForm.prezzo_unitario)
    const qta = parseFloat(liberoForm.quantita)
    const sconto = parseFloat(liberoForm.sconto_percentuale) || 0
    if (!liberoForm.descrizione.trim() || isNaN(prezzo) || isNaN(qta)) return

    onAddCanoneLibero({
      descrizione: liberoForm.descrizione.trim(),
      unita_misura: liberoForm.unita_misura || 'pz',
      prezzo_unitario: prezzo,
      quantita: qta,
      sconto_percentuale: sconto,
      frequenza_valore: parseInt(liberoForm.frequenza_valore) || 1,
      frequenza_unita: liberoForm.frequenza_unita,
      giorni_preavviso: parseInt(liberoForm.giorni_preavviso) || 30,
    })
    setLiberoForm({
      descrizione: '',
      unita_misura: 'pz',
      prezzo_unitario: '',
      quantita: '1',
      sconto_percentuale: '0',
      frequenza_valore: '1',
      frequenza_unita: 'mesi',
      giorni_preavviso: '30',
    })
    setShowLiberoForm(false)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = canoni.findIndex(c => c.id === active.id)
    const newIndex = canoni.findIndex(c => c.id === over.id)
    const newOrder = arrayMove(canoni, oldIndex, newIndex)
    onReorder(newOrder.map(c => c.id))
  }

  const closeAllForms = () => {
    setShowAddForm(false)
    setShowNotaForm(false)
    setShowLiberoForm(false)
  }

  // Componente campi frequenza riusabile
  const FrequenzaFields = ({
    valore, onValoreChange,
    unita, onUnitaChange,
    preavviso, onPreavvisoChange,
  }: {
    valore: string; onValoreChange: (v: string) => void
    unita: FrequenzaUnita; onUnitaChange: (u: FrequenzaUnita) => void
    preavviso: string; onPreavvisoChange: (p: string) => void
  }) => (
    <div className="flex items-center gap-2 mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
      <span className="text-sm text-indigo-700 font-medium">Ogni</span>
      <input
        type="number"
        min="1"
        value={valore}
        onChange={(e) => onValoreChange(e.target.value)}
        className="w-16 px-2 py-1.5 text-center border border-indigo-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <select
        value={unita}
        onChange={(e) => onUnitaChange(e.target.value as FrequenzaUnita)}
        className="px-2 py-1.5 border border-indigo-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
      >
        {(Object.entries(FREQUENZA_LABELS) as [FrequenzaUnita, string][]).map(([key, label]) => (
          <option key={key} value={key}>{label}</option>
        ))}
      </select>
      <span className="text-sm text-gray-400 mx-2">|</span>
      <span className="text-sm text-indigo-700">Preavviso</span>
      <input
        type="number"
        min="1"
        value={preavviso}
        onChange={(e) => onPreavvisoChange(e.target.value)}
        className="w-16 px-2 py-1.5 text-center border border-indigo-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <span className="text-sm text-indigo-700">giorni</span>
    </div>
  )

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-indigo-400">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-indigo-500" />
          Canoni Ricorsivi
        </h3>
        {!disabled && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => { closeAllForms(); setShowNotaForm(true) }}
              className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700"
            >
              <StickyNote className="w-4 h-4" />
              Nota
            </button>
            <button
              onClick={() => { closeAllForms(); setShowLiberoForm(true) }}
              className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700"
            >
              <FileEdit className="w-4 h-4" />
              Canone Libero
            </button>
            <button
              onClick={() => { closeAllForms(); setShowAddForm(true) }}
              className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700"
            >
              <Plus className="w-4 h-4" />
              Da Listino
            </button>
          </div>
        )}
      </div>

      {/* Lista canoni con DND */}
      {canoni.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">
          Nessun canone ricorsivo inserito. Aggiungi canoni dal listino o liberi.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-indigo-200">
                  <th className="py-2 w-8"></th>
                  <th className="py-2 text-left text-xs font-medium text-indigo-500 uppercase">Codice</th>
                  <th className="py-2 text-left text-xs font-medium text-indigo-500 uppercase">Descrizione</th>
                  <th className="py-2 text-right text-xs font-medium text-indigo-500 uppercase w-20">Q.ta</th>
                  <th className="py-2 text-right text-xs font-medium text-indigo-500 uppercase w-24">Prezzo</th>
                  <th className="py-2 text-right text-xs font-medium text-indigo-500 uppercase w-20">Sc. %</th>
                  <th className="py-2 text-right text-xs font-medium text-indigo-500 uppercase w-24">Totale</th>
                  <th className="py-2 text-center text-xs font-medium text-indigo-500 uppercase w-28">Frequenza</th>
                  {!disabled && <th className="py-2 w-10"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <SortableContext
                  items={canoni.map(c => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {canoni.map((canone) => (
                    <SortableCanoneRow
                      key={canone.id}
                      canone={canone}
                      disabled={!!disabled}
                      onUpdate={onUpdateCanone}
                      onUpdateNota={onUpdateNota}
                      onDelete={onDeleteCanone}
                      formatCurrency={formatCurrency}
                    />
                  ))}
                </SortableContext>
              </tbody>
            </table>
          </DndContext>
        </div>
      )}

      {/* Form nota */}
      {showNotaForm && (
        <div className="mt-4 p-4 border border-amber-200 rounded-lg bg-amber-50/50">
          <label className="block text-xs text-gray-500 mb-1">Testo nota / sezione</label>
          <input
            type="text"
            value={notaText}
            onChange={(e) => setNotaText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddNota() }}
            placeholder="es. MANUTENZIONE ANNUALE, Note aggiuntive..."
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => { setShowNotaForm(false); setNotaText('') }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded"
            >
              Annulla
            </button>
            <button
              onClick={handleAddNota}
              disabled={!notaText.trim()}
              className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
            >
              Aggiungi Nota
            </button>
          </div>
        </div>
      )}

      {/* Form canone libero */}
      {showLiberoForm && (
        <div className="mt-4 p-4 border border-indigo-200 rounded-lg bg-indigo-50/50">
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-4">
              <label className="block text-xs text-gray-500 mb-1">Descrizione *</label>
              <input
                type="text"
                value={liberoForm.descrizione}
                onChange={(e) => setLiberoForm({ ...liberoForm, descrizione: e.target.value })}
                placeholder="Descrizione canone..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                autoFocus
              />
            </div>
            <div className="col-span-1">
              <label className="block text-xs text-gray-500 mb-1">U.M.</label>
              <input
                type="text"
                value={liberoForm.unita_misura}
                onChange={(e) => setLiberoForm({ ...liberoForm, unita_misura: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Prezzo *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={liberoForm.prezzo_unitario}
                onChange={(e) => setLiberoForm({ ...liberoForm, prezzo_unitario: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Quantita *</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={liberoForm.quantita}
                onChange={(e) => setLiberoForm({ ...liberoForm, quantita: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Sconto %</label>
              <input
                type="number"
                step="0.5"
                value={liberoForm.sconto_percentuale}
                onChange={(e) => setLiberoForm({ ...liberoForm, sconto_percentuale: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            <div className="col-span-1 flex items-end">
              <span className="text-xs text-gray-400 pb-2">
                {(() => {
                  const p = parseFloat(liberoForm.prezzo_unitario)
                  const q = parseFloat(liberoForm.quantita)
                  const s = parseFloat(liberoForm.sconto_percentuale) || 0
                  if (!isNaN(p) && !isNaN(q)) {
                    return formatCurrency(p * q * (1 - s / 100))
                  }
                  return '-'
                })()}
              </span>
            </div>
          </div>
          <FrequenzaFields
            valore={liberoForm.frequenza_valore}
            onValoreChange={(v) => setLiberoForm({ ...liberoForm, frequenza_valore: v })}
            unita={liberoForm.frequenza_unita}
            onUnitaChange={(u) => setLiberoForm({ ...liberoForm, frequenza_unita: u })}
            preavviso={liberoForm.giorni_preavviso}
            onPreavvisoChange={(p) => setLiberoForm({ ...liberoForm, giorni_preavviso: p })}
          />
          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={() => {
                setShowLiberoForm(false)
                setLiberoForm({
                  descrizione: '', unita_misura: 'pz', prezzo_unitario: '', quantita: '1',
                  sconto_percentuale: '0', frequenza_valore: '1', frequenza_unita: 'mesi', giorni_preavviso: '30'
                })
              }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded"
            >
              Annulla
            </button>
            <button
              onClick={handleAddLibero}
              disabled={!liberoForm.descrizione.trim() || !liberoForm.prezzo_unitario}
              className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Aggiungi Canone
            </button>
          </div>
        </div>
      )}

      {/* Form da listino */}
      {showAddForm && (
        <div className="mt-4 p-4 border border-indigo-200 rounded-lg bg-indigo-50/30">
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-8 relative" ref={dropdownRef}>
              <label className="block text-xs text-gray-500 mb-1">Voce Listino</label>
              <button
                type="button"
                onClick={() => setShowListinoDropdown(!showListinoDropdown)}
                className="w-full flex items-center justify-between px-3 py-2 border border-gray-200 rounded-lg text-left bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <span className={selectedListino ? 'text-gray-900' : 'text-gray-400'}>
                  {selectedListino ? `${selectedListino.codice} - ${selectedListino.descrizione}` : 'Seleziona voce...'}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showListinoDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showListinoDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Cerca nel listino..."
                        value={searchListino}
                        onChange={(e) => setSearchListino(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {availableListino.length === 0 ? (
                      <div className="px-3 py-4 text-sm text-gray-500 text-center">
                        Nessuna voce disponibile
                      </div>
                    ) : (
                      availableListino.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => {
                            setSelectedListino(item)
                            setShowListinoDropdown(false)
                            setSearchListino('')
                          }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-indigo-50 flex justify-between items-center"
                        >
                          <div>
                            <span className="font-mono text-xs bg-gray-100 px-1 rounded mr-2">{item.codice}</span>
                            <span className="text-gray-700">{item.descrizione}</span>
                          </div>
                          <span className="text-gray-500">{formatCurrency(item.prezzo_unitario)}</span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Quantita</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={quantita}
                onChange={(e) => setQuantita(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Prezzo</label>
              <div className="px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-600">
                {selectedListino ? formatCurrency(selectedListino.prezzo_unitario) : '-'}
              </div>
            </div>
          </div>

          <FrequenzaFields
            valore={freqValore}
            onValoreChange={setFreqValore}
            unita={freqUnita}
            onUnitaChange={setFreqUnita}
            preavviso={giorniPreavviso}
            onPreavvisoChange={setGiorniPreavviso}
          />

          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={() => {
                setShowAddForm(false)
                setSelectedListino(null)
                setQuantita('1')
                setFreqValore('1')
                setFreqUnita('mesi')
                setGiorniPreavviso('30')
              }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded"
            >
              Annulla
            </button>
            <button
              onClick={handleAdd}
              disabled={!selectedListino}
              className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Aggiungi Canone
            </button>
          </div>
        </div>
      )}

      {/* Totale */}
      <div className="border-t border-indigo-200 mt-4 pt-4">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-gray-900">TOTALE CANONI RICORSIVI</span>
          <span className="text-xl font-bold text-indigo-600">{formatCurrency(totaleCanoni)}</span>
        </div>
      </div>
    </div>
  )
}
